/**
 * Визуальный редактор схемы зала.
 *
 * Сетка в 20 пикселей — не украшение: она делает расстановку опрятной
 * без линеек и выравниваний, а координаты остаются целыми числами,
 * которые не «плывут» между экранами разной ширины.
 *
 * Столы только двигаются: создаются и удаляются они в справочнике зала,
 * потому что стол связан с заказами и не должен исчезать из истории
 * от движения мышью.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { HallPlan, HallPlanList, PlanShape, PlanTable } from '@dastarhan/contracts';

const CELL = 20;
const COLS = 44;
const ROWS = 28;

/**
 * Мебель и ориентиры, которые владелец ставит сам: без сцены,
 * барной стойки и входа план не читается как помещение.
 * Размеры по умолчанию — в клетках сетки, под типичную вещь:
 * диван длинный и узкий, сцена крупная, окно — полоса на стене.
 */
const SHAPE_META: Record<string, { btn: string; w: number; h: number; text: string | null; color: string }> = {
  RECT:     { btn: 'Стена',    w: 8,  h: 1, text: null,            color: 'd8dbe0' },
  SOFA:     { btn: 'Диван',    w: 6,  h: 1, text: null,            color: 'd8dbe0' },
  STAGE:    { btn: 'Сцена',    w: 8,  h: 4, text: 'Сцена',         color: 'd8dbe0' },
  BAR:      { btn: 'Барная стойка', w: 10, h: 2, text: 'Барная стойка', color: 'd8dbe0' },
  ENTRANCE: { btn: 'Вход',     w: 3,  h: 2, text: 'Вход',          color: 'd8dbe0' },
  WC:       { btn: 'WC',       w: 3,  h: 2, text: 'WC',            color: 'd8dbe0' },
  WINDOW:   { btn: 'Окно',     w: 6,  h: 1, text: null,            color: 'd8dbe0' },
  PLANT:    { btn: 'Растение', w: 2,  h: 2, text: null,            color: 'd8dbe0' },
  CIRCLE:   { btn: 'Круг',     w: 3,  h: 3, text: null,            color: 'd8dbe0' },
  TEXT:     { btn: 'Подпись',  w: 5,  h: 1, text: 'Подпись',       color: '62666d' },
};

/** Виды, где цвет решает дизайн-система; вручную красятся только
 *  стены, круги и подписи — сырая геометрия без смысла. */
const SEMANTIC = new Set(['SOFA', 'STAGE', 'BAR', 'ENTRANCE', 'WC', 'WINDOW', 'PLANT']);
/** У этих видов есть подпись на плане — её можно переназвать. */
const LABELED = new Set(['STAGE', 'BAR', 'ENTRANCE', 'WC', 'TEXT']);

type Draft = {
  tables: PlanTable[];
  shapes: (PlanShape & { isNew?: boolean })[];
};

type Sel = { kind: 'table' | 'shape'; id: string } | null;

const API = '/api/v1';

async function call<T>(path: string, token: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    method: opts.method ?? 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json() as Promise<T>;
}

export function HallPlanEditor({ token, locationId }: { token: string; locationId: string }) {
  const qc = useQueryClient();
  const [hallIdx, setHallIdx] = useState(0);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [sel, setSel] = useState<Sel>(null);
  const [dirty, setDirty] = useState(false);
  const boardRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ id: string; kind: 'table' | 'shape'; dx: number; dy: number } | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['hall-plan', locationId],
    queryFn: () => call<HallPlanList>(`/hall-plan?locationId=${locationId}`, token),
  });

  const hall: HallPlan | undefined = data?.halls[hallIdx];

  useEffect(() => {
    if (hall) {
      setDraft({ tables: hall.tables.map((t) => ({ ...t })), shapes: hall.shapes.map((s) => ({ ...s })) });
      setDirty(false); setSel(null);
    }
  }, [hall?.hallId, data]);

  const save = useMutation({
    mutationFn: () => call('/hall-plan', token, {
      method: 'POST',
      body: {
        hallId: hall!.hallId,
        tables: draft!.tables.map((t) => ({ id: t.id, x: t.x, y: t.y, w: t.w, h: t.h, shape: t.shape })),
        shapes: draft!.shapes.map((s) => ({
          kind: s.kind, x: s.x, y: s.y, w: s.w, h: s.h,
          text: s.text, color: s.color, rotate: s.rotate,
        })),
      },
    }),
    onSuccess: () => {
      setDirty(false);
      void qc.invalidateQueries({ queryKey: ['hall-plan', locationId] });
    },
  });

  /** Перевод координат мыши в клетки сетки — с прилипанием. */
  const toCell = (e: { clientX: number; clientY: number }) => {
    const r = boardRef.current!.getBoundingClientRect();
    return {
      x: Math.max(0, Math.round((e.clientX - r.left) / CELL)),
      y: Math.max(0, Math.round((e.clientY - r.top) / CELL)),
    };
  };

  const startDrag = (kind: 'table' | 'shape', id: string, e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    const c = toCell(e);
    const item = kind === 'table'
      ? draft!.tables.find((t) => t.id === id)
      : draft!.shapes.find((s) => s.id === id);
    dragRef.current = { id, kind, dx: c.x - (item?.x ?? 0), dy: c.y - (item?.y ?? 0) };
    setSel({ kind, id });
  };

  const onMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || !draft) return;
    const c = toCell(e);
    const nx = Math.max(0, Math.min(COLS - 1, c.x - d.dx));
    const ny = Math.max(0, Math.min(ROWS - 1, c.y - d.dy));
    setDraft(d.kind === 'table'
      ? { ...draft, tables: draft.tables.map((t) => (t.id === d.id ? { ...t, x: nx, y: ny } : t)) }
      : { ...draft, shapes: draft.shapes.map((s) => (s.id === d.id ? { ...s, x: nx, y: ny } : s)) });
    setDirty(true);
  };

  const endDrag = () => { dragRef.current = null; };

  const addShape = (kind: PlanShape['kind']) => {
    if (!draft) return;
    // Запасной вид на случай, если в схеме появится мебель, о которой
    // редактор ещё не знает: лучше поставить безымянный прямоугольник,
    // чем уронить экран владельцу
    const meta = SHAPE_META[kind] ?? { btn: kind, w: 4, h: 2, text: null, color: 'd8dbe0' };
    const id = `new-${Math.random().toString(36).slice(2, 9)}`;
    setDraft({
      ...draft,
      shapes: [...draft.shapes, {
        id, kind, x: 2, y: 2,
        w: meta.w, h: meta.h,
        text: meta.text,
        color: meta.color,
        rotate: 0, isNew: true,
      }],
    });
    setSel({ kind: 'shape', id });
    setDirty(true);
  };

  /** Правки стола и фигуры разделены: у них разные поля и разная судьба. */
  const patchTable = (patch: Partial<PlanTable> & { x?: number | null; y?: number | null }) => {
    if (!draft || sel?.kind !== 'table') return;
    setDraft({
      ...draft,
      tables: draft.tables.map((t) => (t.id === sel.id ? { ...t, ...patch } : t)),
    });
    setDirty(true);
  };

  const patchShape = (patch: Partial<PlanShape>) => {
    if (!draft || sel?.kind !== 'shape') return;
    setDraft({
      ...draft,
      shapes: draft.shapes.map((s) => (s.id === sel.id ? { ...s, ...patch } : s)),
    });
    setDirty(true);
  };

  const patchSize = (patch: { w?: number; h?: number }) => {
    if (sel?.kind === 'table') patchTable(patch);
    else patchShape(patch);
  };

  const removeSel = () => {
    if (!draft || !sel) return;
    if (sel.kind === 'table') {
      // стол не удаляем — снимаем с плана: он остаётся в справочнике
      patchTable({ x: null, y: null });
    } else {
      setDraft({ ...draft, shapes: draft.shapes.filter((s) => s.id !== sel.id) });
    }
    setSel(null);
    setDirty(true);
  };

  const offPlan = useMemo(() => draft?.tables.filter((t) => t.x === null) ?? [], [draft]);
  const selected = sel && draft
    ? (sel.kind === 'table'
      ? draft.tables.find((t) => t.id === sel.id)
      : draft.shapes.find((s) => s.id === sel.id))
    : null;

  if (isPending) return <p className="hint">Загружаем схему…</p>;
  if (!data?.halls.length) {
    return <p className="hint">Сначала создайте зал в разделе «Зал» — потом его можно нарисовать.</p>;
  }
  if (!draft || !hall) return null;

  return (
    <section className="menu-cat">
      <header>
        <h2>Схема зала</h2>
        <div className="plan-tools">
          {data.halls.length > 1 && (
            <select value={hallIdx} onChange={(e) => setHallIdx(Number(e.target.value))}>
              {data.halls.map((h, i) => <option key={h.hallId} value={i}>{h.hallName}</option>)}
            </select>
          )}
          <button className="btn primary" disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}>
            {save.isPending ? 'Сохраняем…' : dirty ? 'Сохранить схему' : 'Сохранено'}
          </button>
        </div>
      </header>

      {/* Палитра мебели: владелец ставит не только столы — сцена, бар,
          вход и окна делают план похожим на его зал, а не на сетку */}
      <div className="plan-palette" role="group" aria-label="Добавить на схему">
        <span className="plan-palette-label">Добавить:</span>
        {(Object.keys(SHAPE_META) as PlanShape['kind'][]).map((k) => (
          <button key={k} className="btn" onClick={() => addShape(k)}>
            + {SHAPE_META[k]?.btn ?? k}
          </button>
        ))}
      </div>

      <p className="hint">
        Перетащите стол или мебель на своё место. Сцена, бар и вход
        помогают официанту узнать зал с одного взгляда — на заказы
        они не влияют.
      </p>

      <div className="plan-wrap">
        <div
          ref={boardRef}
          className="plan-board"
          style={{ width: COLS * CELL, height: ROWS * CELL }}
          onPointerMove={onMove}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
          onClick={(e) => { if (e.target === boardRef.current) setSel(null); }}
        >
          {draft.shapes.map((s) => {
            const semantic = SEMANTIC.has(s.kind);
            const label = s.kind === 'TEXT'
              ? s.text
              : (s.text ?? SHAPE_META[s.kind]?.text ?? null);
            return (
              <div
                key={s.id}
                className={`plan-shape ${s.kind.toLowerCase()} ${sel?.id === s.id ? 'sel' : ''}`}
                style={{
                  left: s.x * CELL, top: s.y * CELL,
                  width: s.w * CELL, height: s.h * CELL,
                  ...(semantic ? {} : s.kind === 'TEXT'
                    ? { color: `#${s.color}` }
                    : { background: `#${s.color}` }),
                  transform: s.rotate ? `rotate(${s.rotate}deg)` : undefined,
                }}
                onPointerDown={(e) => startDrag('shape', s.id, e)}
              >
                {semantic || s.kind === 'TEXT' ? label : null}
              </div>
            );
          })}

          {draft.tables.filter((t) => t.x !== null).map((t) => (
            <div
              key={t.id}
              className={`plan-table ${t.shape.toLowerCase()} ${sel?.id === t.id ? 'sel' : ''}`}
              style={{
                left: t.x! * CELL, top: t.y! * CELL,
                width: t.w * CELL, height: t.h * CELL,
              }}
              onPointerDown={(e) => startDrag('table', t.id, e)}
              title={`${t.name} · мест ${t.seats}`}
            >
              <b>{t.name}</b>
              <span>{t.seats}</span>
            </div>
          ))}
        </div>

        <aside className="plan-side">
          {offPlan.length > 0 && (
            <>
              <h3>Не на схеме</h3>
              <p className="hint">Нажмите, чтобы поставить в свободный угол</p>
              <div className="plan-pool">
                {offPlan.map((t) => (
                  <button key={t.id} className="pool-item" onClick={() => {
                    // Свободное место под уже расставленными: раньше все
                    // новые столы падали в один угол стопкой друг на друге
                    const placedNow = draft.tables.filter((x) => x.x !== null);
                    const belowAll = placedNow.length
                      ? Math.max(...placedNow.map((x) => (x.y ?? 0) + x.h)) + 1 : 1;
                    const spot = { x: 1, y: Math.min(ROWS - 3, belowAll) };
                    setDraft({
                      ...draft,
                      tables: draft.tables.map((x) => (x.id === t.id ? { ...x, x: spot.x, y: spot.y } : x)),
                    });
                    setSel({ kind: 'table', id: t.id });
                    setDirty(true);
                  }}>{t.name}</button>
                ))}
              </div>
            </>
          )}

          {selected && (
            <>
              <h3>
                {sel!.kind === 'table'
                  ? (selected as PlanTable).name
                  : SHAPE_META[(selected as PlanShape).kind]?.btn ?? 'Элемент'}
              </h3>
              <label>Ширина
                <input type="range" min={1} max={sel!.kind === 'shape' ? 30 : 16} value={selected.w}
                  onChange={(e) => patchSize({ w: Number(e.target.value) })} />
              </label>
              <label>Высота
                <input type="range" min={1} max={16} value={selected.h}
                  onChange={(e) => patchSize({ h: Number(e.target.value) })} />
              </label>

              {sel!.kind === 'table' && (
                <label>Форма
                  <select value={(selected as PlanTable).shape}
                    onChange={(e) => patchTable({ shape: e.target.value as 'RECT' })}>
                    <option value="RECT">Прямоугольный</option>
                    <option value="CIRCLE">Круглый</option>
                  </select>
                </label>
              )}

              {sel!.kind === 'shape' && (
                <>
                  {/* Подпись меняется там, где она есть: «Сцена» можно
                      переназвать в «Стендап», WC — в «Гардероб» */}
                  {LABELED.has((selected as PlanShape).kind) && (
                    <label>Подпись на плане
                      <input value={(selected as PlanShape).text ?? ''}
                        placeholder={SHAPE_META[(selected as PlanShape).kind]?.text ?? ''}
                        onChange={(e) => patchShape({ text: e.target.value })} />
                    </label>
                  )}
                  {/* Барная стойка и диван бывают под углом — поворот
                      есть у любой мебели, кроме текста */}
                  {(selected as PlanShape).kind !== 'TEXT' && (
                    <label>Поворот
                      <input type="range" min={-90} max={90} step={15}
                        value={(selected as PlanShape).rotate}
                        onChange={(e) => patchShape({ rotate: Number(e.target.value) })} />
                    </label>
                  )}
                  {/* Цвет — только у сырой геометрии: мебель красит
                      дизайн-система, чтобы план оставался спокойным
                      и статусы столов читались поверх него */}
                  {!SEMANTIC.has((selected as PlanShape).kind) && (
                    <label>Цвет
                      <input type="color" value={`#${(selected as PlanShape).color}`}
                        onChange={(e) => patchShape({ color: e.target.value.slice(1) })} />
                    </label>
                  )}
                </>
              )}

              <button className="btn ghost" onClick={removeSel}>
                {sel!.kind === 'table' ? 'Убрать со схемы' : 'Удалить элемент'}
              </button>
            </>
          )}

          {!selected && offPlan.length === 0 && (
            <p className="hint">Выберите стол или элемент, чтобы настроить.</p>
          )}
        </aside>
      </div>
    </section>
  );
}
